// client/hooks/widgets/useWidgetConfig.js
// ✅ Hook für Widget-Konfiguration (Load, Save, Update)
import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../constants/api";
import { getWidgetById } from "../../components/widgets/registry.js";
import { useAuth } from "../../components/ui/AuthContext.jsx";

const ACADEMY_WIDGET_ID = "academy-progress";

function visibleWidgets(list) {
  return (list || []).filter((w) => w?.id && getWidgetById(w.id));
}

/** Entfernte/unbekannte IDs aus gespeicherten Rails streichen (Cache + API). */
function sanitizeWidgetConfig(config) {
  if (!config?.widgets) return config;
  return {
    ...config,
    widgets: {
      ...config.widgets,
      left: visibleWidgets(config.widgets.left),
      right: visibleWidgets(config.widgets.right),
    },
  };
}

/** Pflicht-Academy-Widget oben links einfügen, solange needAcademy. */
function ensureAcademyWidget(config, needAcademy) {
  if (!config?.widgets || !needAcademy) return config;
  const left = config.widgets.left || [];
  const right = config.widgets.right || [];
  const has =
    left.some((w) => w.id === ACADEMY_WIDGET_ID) ||
    right.some((w) => w.id === ACADEMY_WIDGET_ID);
  if (has) {
    // Aufgeklappt halten
    return {
      ...config,
      widgets: {
        ...config.widgets,
        left: left.map((w) =>
          w.id === ACADEMY_WIDGET_ID ? { ...w, minimized: false } : w
        ),
        right: right.map((w) =>
          w.id === ACADEMY_WIDGET_ID ? { ...w, minimized: false } : w
        ),
      },
    };
  }
  return {
    ...config,
    widgets: {
      ...config.widgets,
      left: [{ id: ACADEMY_WIDGET_ID, minimized: false }, ...left],
    },
  };
}

const CACHE_KEY = "wrtk-widget-config";
const CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

/**
 * Hook für Widget-Konfiguration
 * - Lädt Config aus API (mit LocalStorage-Cache als Fallback)
 * - Bietet Funktionen zum Aktualisieren
 * - Optimistic Updates für bessere UX
 */
export function useWidgetConfig() {
  const { needAcademy } = useAuth();
  const [config, setConfig] = useState(null);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);

  const applyConfig = useCallback(
    (raw) => ensureAcademyWidget(sanitizeWidgetConfig(raw), needAcademy),
    [needAcademy]
  );

  // Config laden
  const loadConfig = useCallback(async (force = false) => {
    // Cache prüfen (wenn nicht force)
    if (!force) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            setConfig(applyConfig(data.config));
            setAvailableWidgets(data.available_widgets || []);
            setIsLoading(false);
            // Im Hintergrund trotzdem neu laden
          }
        }
      } catch {
        // Cache invalid - ignorieren
      }
    }

    // API Call
    abortRef.current?.abort?.();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await api.get("/profil/widgets", {
        signal: ctrl.signal,
        silent: true,
      });

      if (res.data?.ok) {
        setConfig(applyConfig(res.data.config));
        setAvailableWidgets(res.data.available_widgets || []);
        setError(null);

        // Cache updaten
        try {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              data: res.data,
              timestamp: Date.now(),
            })
          );
        } catch {
          // LocalStorage voll - ignorieren
        }
      }
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      setError("Konfiguration konnte nicht geladen werden");
      console.error("Widget config load error:", e);
    } finally {
      setIsLoading(false);
    }
  }, [applyConfig]);

  // Initial laden
  useEffect(() => {
    loadConfig();
    return () => abortRef.current?.abort?.();
  }, [loadConfig]);

  // Bei needAcademy-Wechsel Widget nachziehen
  useEffect(() => {
    setConfig((prev) => (prev ? applyConfig(prev) : prev));
  }, [needAcademy, applyConfig]);

  // Config speichern (Partial Update)
  const saveConfig = useCallback(
    async (updates) => {
      if (!config) return false;

      // Optimistic Update
      const previousConfig = { ...config };
      setConfig((prev) => applyConfig({ ...prev, ...updates }));
      setIsSaving(true);

      try {
        const res = await api.patch("/profil/widgets", updates, { silent: true });

        if (res.data?.ok) {
          // Server-Antwort übernehmen (falls Server was geändert hat)
          setConfig(applyConfig(res.data.config));

          // Cache invalidieren
          try {
            localStorage.removeItem(CACHE_KEY);
          } catch {}

          return true;
        } else {
          // Rollback bei Fehler
          setConfig(previousConfig);
          setError(res.data?.error || "Speichern fehlgeschlagen");
          return false;
        }
      } catch (e) {
        // Rollback bei Fehler
        setConfig(previousConfig);
        setError("Speichern fehlgeschlagen");
        console.error("Widget config save error:", e);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [config, applyConfig]
  );

  // === Convenience-Funktionen ===

  /**
   * Widget zu einer Rail hinzufügen
   */
  const addWidget = useCallback(
    async (widgetId, rail = "left") => {
      if (!config?.widgets) return false;

      const newWidgets = { ...config.widgets };
      const targetRail = [...(newWidgets[rail] || [])];

      // Prüfen ob Widget schon existiert
      const existsLeft = newWidgets.left?.some((w) => w.id === widgetId);
      const existsRight = newWidgets.right?.some((w) => w.id === widgetId);
      if (existsLeft || existsRight) {
        setError("Widget ist bereits aktiv");
        return false;
      }

      targetRail.push({ id: widgetId, minimized: false });
      newWidgets[rail] = targetRail;

      return saveConfig({ widgets: newWidgets });
    },
    [config, saveConfig]
  );

  /**
   * Widget entfernen
   */
  const removeWidget = useCallback(
    async (widgetId) => {
      if (!config?.widgets) return false;
      if (widgetId === ACADEMY_WIDGET_ID && needAcademy) {
        setError("Academy-Widget bleibt bis zum Abschluss sichtbar.");
        return false;
      }

      const newWidgets = {
        left: (config.widgets.left || []).filter((w) => w.id !== widgetId),
        right: (config.widgets.right || []).filter((w) => w.id !== widgetId),
      };

      return saveConfig({ widgets: newWidgets });
    },
    [config, saveConfig, needAcademy]
  );

  /**
   * Widget minimieren/maximieren
   */
  const toggleMinimize = useCallback(
    async (widgetId) => {
      if (!config?.widgets) return false;
      if (widgetId === ACADEMY_WIDGET_ID && needAcademy) {
        // Pflicht-Widget bewusst ausgeklappt lassen
        return false;
      }

      const newWidgets = { ...config.widgets };

      // In beiden Rails suchen
      for (const rail of ["left", "right"]) {
        const idx = newWidgets[rail]?.findIndex((w) => w.id === widgetId);
        if (idx !== undefined && idx >= 0) {
          newWidgets[rail] = [...newWidgets[rail]];
          newWidgets[rail][idx] = {
            ...newWidgets[rail][idx],
            minimized: !newWidgets[rail][idx].minimized,
          };
          break;
        }
      }

      return saveConfig({ widgets: newWidgets });
    },
    [config, saveConfig, needAcademy]
  );

  /**
   * Widget-Reihenfolge ändern (für Drag & Drop)
   */
  const reorderWidgets = useCallback(
    async (sourceRail, sourceIndex, targetRail, targetIndex) => {
      if (!config?.widgets) return false;

      const newWidgets = {
        left: [...(config.widgets.left || [])],
        right: [...(config.widgets.right || [])],
      };

      // Widget aus Quelle entfernen
      const [moved] = newWidgets[sourceRail].splice(sourceIndex, 1);

      // Widget am Ziel einfügen
      newWidgets[targetRail].splice(targetIndex, 0, moved);

      return saveConfig({ widgets: newWidgets });
    },
    [config, saveConfig]
  );

  /**
   * Rail aktivieren/deaktivieren
   */
  const toggleRail = useCallback(
    async (rail) => {
      if (!config) return false;

      const key = rail === "left" ? "left_enabled" : "right_enabled";
      return saveConfig({ [key]: !config[key] });
    },
    [config, saveConfig]
  );

  /**
   * Rail-Breite ändern
   */
  const setRailWidth = useCallback(
    async (width) => {
      const clampedWidth = Math.min(400, Math.max(240, width));
      return saveConfig({ rail_width: clampedWidth });
    },
    [saveConfig]
  );

  return {
    // State
    config,
    availableWidgets,
    isLoading,
    isSaving,
    error,

    // Actions
    loadConfig,
    saveConfig,
    addWidget,
    removeWidget,
    toggleMinimize,
    reorderWidgets,
    toggleRail,
    setRailWidth,

    // Computed
    leftWidgets: visibleWidgets(config?.widgets?.left),
    rightWidgets: visibleWidgets(config?.widgets?.right),
    railWidth: config?.rail_width || 300,
    leftEnabled: config?.left_enabled ?? true,
    rightEnabled: config?.right_enabled ?? true,
  };
}

export default useWidgetConfig;
